import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { Lock, LogOut, RefreshCw, Calendar, Users, DollarSign, Bed, Loader2 } from 'lucide-react';
import { WellnessNav, WellnessFooter } from './WellnessLanding';
import { WELLNESS_API, ADMIN_TOKEN_KEY, fmtUSD } from './_shared';

export default function WellnessAdmin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(ADMIN_TOKEN_KEY) || '');
  const onLogin = (t) => { sessionStorage.setItem(ADMIN_TOKEN_KEY, t); setToken(t); };
  const logout = () => { sessionStorage.removeItem(ADMIN_TOKEN_KEY); setToken(''); };
  return token
    ? <AdminDashboard token={token} logout={logout} />
    : <AdminLogin onLogin={onLogin} />;
}

function AdminLogin({ onLogin }) {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    try {
      const r = await axios.post(`${WELLNESS_API}/admin/login`, { password });
      onLogin(r.data.token);
      toast.success('Welcome back');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invalid password');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#faf7ef] flex flex-col" style={{ fontFamily: 'Georgia, serif' }} data-testid="admin-login">
      <Toaster richColors position="top-right" />
      <WellnessNav />
      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <form onSubmit={submit} className="bg-white rounded-2xl border border-[#e0d6c2] shadow-lg p-8 w-full max-w-sm">
          <div className="w-12 h-12 rounded-full bg-[#2f5d50] flex items-center justify-center mb-4 mx-auto">
            <Lock className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-xl font-bold text-center mb-1">Staff Login</h2>
          <p className="text-xs text-[#6b6358] text-center mb-6 font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>Dr Raju Wellness Admin</p>
          <input
            type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="Admin password"
            className="w-full h-11 px-3 rounded-xl border border-[#e0d6c2] text-sm focus:border-[#2f5d50] focus:outline-none mb-4 font-sans"
            style={{ fontFamily: 'Inter, sans-serif' }}
            data-testid="admin-password"
            autoFocus
          />
          <button type="submit" disabled={loading} className="w-full h-11 rounded-xl bg-[#2f5d50] hover:bg-[#244a40] text-white text-sm font-semibold transition flex items-center justify-center gap-2 disabled:opacity-50 font-sans" style={{ fontFamily: 'Inter, sans-serif' }} data-testid="admin-login-btn">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Login
          </button>
        </form>
      </div>
      <WellnessFooter />
    </div>
  );
}

function AdminDashboard({ token, logout }) {
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [s, b] = await Promise.all([
        axios.get(`${WELLNESS_API}/admin/stats`, { headers }),
        axios.get(`${WELLNESS_API}/admin/bookings`, { headers }),
      ]);
      setStats(s.data);
      setBookings(b.data.bookings || []);
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        toast.error('Session expired');
        logout();
      } else {
        toast.error('Failed to load admin data');
      }
    } finally {
      setLoading(false);
    }
  }

  function refresh() {
    setLoading(true);
    load();
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await load();
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="min-h-screen bg-[#faf7ef]" style={{ fontFamily: 'Georgia, serif' }} data-testid="admin-dashboard">
      <Toaster richColors position="top-right" />
      <WellnessNav />

      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-7">
          <div>
            <h1 className="text-3xl font-bold">Wellness Admin</h1>
            <p className="text-xs text-[#6b6358] font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>Bookings & occupancy overview</p>
          </div>
          <div className="flex gap-2 font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
            <button onClick={refresh} className="px-4 py-2 rounded-full text-xs font-semibold bg-white border border-[#e0d6c2] hover:bg-[#f3ece0] flex items-center gap-1.5" data-testid="admin-refresh">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />Refresh
            </button>
            <button onClick={logout} className="px-4 py-2 rounded-full text-xs font-semibold text-[#a14b3a] hover:bg-[#f3ece0] flex items-center gap-1.5" data-testid="admin-logout">
              <LogOut className="w-3.5 h-3.5" />Logout
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-7">
          <StatCard icon={Calendar} label="Total bookings" value={stats?.total_bookings ?? '—'} accent="#2f5d50" />
          <StatCard icon={DollarSign} label="Revenue (USD)" value={stats ? fmtUSD(stats.total_revenue_usd) : '—'} accent="#c89b3c" />
          <StatCard icon={Users} label="In-house today" value={stats?.in_house_today ?? '—'} accent="#5b8b7e" />
          <StatCard icon={Bed} label="Upcoming" value={stats?.upcoming_bookings ?? '—'} accent="#a14b3a" />
        </div>

        {/* Occupancy */}
        <div className="bg-white rounded-2xl border border-[#e0d6c2] p-5 mb-7 font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
          <p className="text-[10px] uppercase tracking-widest text-[#c89b3c] font-semibold mb-3">Occupancy next 14 days</p>
          <div className="space-y-3">
            {stats?.occupancy?.map((o) => (
              <div key={o.room_id}>
                <div className="flex justify-between text-xs font-semibold mb-1">
                  <span>{o.room_name} <span className="text-[#6b6358] font-normal">· {o.booked_units_today}/{o.total_units} today</span></span>
                  <span>{o.occupancy_next_14d_pct}%</span>
                </div>
                <div className="h-2 rounded-full bg-[#f3ece0] overflow-hidden">
                  <div className="h-full bg-[#2f5d50]" style={{ width: `${Math.min(100, o.occupancy_next_14d_pct)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bookings */}
        <div className="bg-white rounded-2xl border border-[#e0d6c2] overflow-hidden">
          <div className="px-5 py-4 border-b border-[#e0d6c2] font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
            <p className="text-[10px] uppercase tracking-widest text-[#c89b3c] font-semibold">All bookings ({bookings.length})</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
              <thead className="bg-[#f3ece0] text-[10px] uppercase tracking-widest text-[#6b6358]">
                <tr>
                  <th className="text-left px-4 py-2">Code</th>
                  <th className="text-left px-4 py-2">Guest</th>
                  <th className="text-left px-4 py-2">Country</th>
                  <th className="text-left px-4 py-2">Room</th>
                  <th className="text-left px-4 py-2">Stay</th>
                  <th className="text-left px-4 py-2">Treatments</th>
                  <th className="text-right px-4 py-2">Total</th>
                  <th className="text-left px-4 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {bookings.length === 0 && (
                  <tr><td colSpan="8" className="text-center py-10 text-[#6b6358]">No bookings yet</td></tr>
                )}
                {bookings.map((b) => (
                  <tr key={b.booking_id} className="border-t border-[#e0d6c2] hover:bg-[#faf7ef]/60" data-testid={`booking-row-${b.confirmation_code}`}>
                    <td className="px-4 py-3 text-[#c89b3c] font-bold text-xs tracking-wider">{b.confirmation_code}</td>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-xs">{b.guest?.full_name}</p>
                      <p className="text-[10px] text-[#6b6358]">{b.guest?.email}</p>
                    </td>
                    <td className="px-4 py-3 text-xs">{b.guest?.country}</td>
                    <td className="px-4 py-3 text-xs">{b.room_name}</td>
                    <td className="px-4 py-3 text-xs">{b.check_in} → {b.check_out}<br /><span className="text-[#6b6358]">{b.nights} nights</span></td>
                    <td className="px-4 py-3 text-xs">{(b.treatments_summary || []).map((t) => t.name).join(', ') || '—'}</td>
                    <td className="px-4 py-3 text-right font-bold text-xs">{fmtUSD(b.grand_total)}</td>
                    <td className="px-4 py-3"><span className="text-[10px] px-2 py-1 rounded-full bg-[#2f5d50]/10 text-[#2f5d50] font-semibold">{b.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <WellnessFooter />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white rounded-2xl border border-[#e0d6c2] p-5 font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
      <div className="flex items-center justify-between mb-3">
        <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: `${accent}1a` }}>
          <Icon className="w-4 h-4" style={{ color: accent }} />
        </div>
      </div>
      <p className="text-2xl font-bold text-[#2a2620]">{value}</p>
      <p className="text-[10px] uppercase tracking-widest text-[#6b6358] font-semibold mt-1">{label}</p>
    </div>
  );
}
